// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { get, post } from './api';
import { ENDPOINTS } from '../utils/constants';
import { drainCursor, MAX_PAGE_SIZE } from './cursorPages';
import type { AnomalyApiPage, AnomalyApiRecord } from '../types';

export interface ListAnomaliesFilters {
  alarmCode?: string;
  alarmId?: string;
  alarmRuleId?: string;
  organizationId?: string;
  organizationKey?: string;
  siteKey?: string;
  equipmentId?: string;
  equipmentKey?: string;
  equipmentType?: string;
  pointId?: string;
  pointKey?: string;
  alarmStatus?: string;
  assignedTo?: string;
  acknowledgedBy?: string;
  pageSize?: number;
  cursor?: string;
}

const FILTER_TO_QUERY_PARAM: Record<keyof ListAnomaliesFilters, string> = {
  alarmCode: 'alarm_code',
  alarmId: 'alarm_id',
  alarmRuleId: 'alarm_rule_id',
  organizationId: 'organization_id',
  organizationKey: 'organization_key',
  siteKey: 'site_key',
  equipmentId: 'equipment_id',
  equipmentKey: 'equipment_key',
  equipmentType: 'equipment_type',
  pointId: 'point_id',
  pointKey: 'point_key',
  alarmStatus: 'alarm_status',
  assignedTo: 'assigned_to',
  acknowledgedBy: 'acknowledged_by',
  pageSize: 'page_size',
  cursor: 'cursor',
};

/**
 * List a site's anomalies (event_type != 'alarm' rows), optionally filtered by
 * any listed column. Pagination is cursor-based — pass the previous response's
 * `context.next_cursor` back as `cursor` for the next page. Fetches a single
 * page only (does not auto-follow the cursor).
 */
export const listAnomalies = (
  siteId: string,
  filters: ListAnomaliesFilters = {}
): Promise<AnomalyApiPage> => {
  const params = new URLSearchParams();
  (Object.keys(filters) as (keyof ListAnomaliesFilters)[]).forEach(key => {
    const value = filters[key];
    if (value !== undefined && value !== null) {
      params.set(FILTER_TO_QUERY_PARAM[key], String(value));
    }
  });
  const qs = params.toString();
  const base = ENDPOINTS.ANOMALIES(siteId);
  return get<AnomalyApiPage>(qs ? `${base}?${qs}` : base);
};

/**
 * Every current anomaly for a site, following `next_cursor` to the end.
 *
 * Same reasoning as alarmApiService's `listAllAlarms`: upstream only orders
 * `updated_at DESC`, so a severity ordering (and an honest count) needs the whole
 * set, not the first page.
 */
export const listAllAnomalies = async (
  siteId: string,
  filters: ListAnomaliesFilters = {}
): Promise<AnomalyApiRecord[]> => {
  const { rows, truncated } = await drainCursor<AnomalyApiRecord>(
    cursor => listAnomalies(siteId, { ...filters, pageSize: MAX_PAGE_SIZE, cursor }),
    { label: `listAllAnomalies(${siteId})` }
  );
  // A capped drain is a floor, not a total: fail loudly rather than count it.
  if (truncated) throw new Error(`Anomaly list for ${siteId} is incomplete (page cap reached)`);
  return rows;
};

/**
 * Promote one anomaly to an alarm — flips the row's `event_type` from `finding`
 * to `alarm`. Takes no body and resolves with nothing (upstream 204).
 *
 * Addressed by `alarm_id`, the per-occurrence identifier. It used to be keyed by
 * `alarm_code` (the rule), which promoted every finding row at the site sharing
 * that rule in one unbounded UPDATE.
 *
 * The only write an anomaly supports: promotion changes classification only,
 * leaving `alarm_status`/`assigned_to` untouched, so the alarm lifecycle (ack,
 * work orders) only becomes available once the row is an alarm.
 *
 * Rejects with an ApiError carrying the status: 404 (no such finding), 409
 * (already promoted — the ambiguous-code case no longer exists) or 502 (Event
 * Hub publish failed and the write was compensated back — retryable).
 */
export const promoteAnomaly = (siteId: string, alarmId: string): Promise<void> =>
  post<void>(ENDPOINTS.ANOMALY_PROMOTE(siteId, alarmId));
